import axios from 'axios'
import {
  Ticket,
  TicketMessage,
  TicketTag,
  AdminUser,
  TicketStats,
  TaskStats,
  TicketsQueryResponse,
  TicketDetailResponse,
  TicketStatus,
  TicketPriority
} from './_models'
import {getAdminToken} from '../../auth/_requests'

const API_URL = process.env.REACT_APP_API_URL

// Create axios instance with credentials for session-based auth
const adminApi = axios.create({
  withCredentials: true
})

// Add request interceptor to include admin token
adminApi.interceptors.request.use((config) => {
  const token = getAdminToken()
  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

// ============================================
// ADMIN TICKET ENDPOINTS
// ============================================

// Get ticket stats for dashboard
export function getTicketStats(): Promise<{ data: TicketStats }> {
  return adminApi.get(`${API_URL}/admin/tickets/stats`)
}

// Get tickets list with filters
export function getTickets(params: {
  status?: TicketStatus
  priority?: TicketPriority
  assigned_to?: string
  tag?: string
  search?: string
  unassigned?: boolean
  my_tickets?: boolean
  admin_unread?: boolean
  follow_up_due?: boolean
  department?: string
  is_internal?: boolean
  page?: number
  limit?: number
  sort?: string
}): Promise<{ data: TicketsQueryResponse }> {
  return adminApi.get(`${API_URL}/admin/tickets`, { params })
}

// Get my assigned tickets
export function getMyTickets(): Promise<{ data: { tickets: Ticket[] } }> {
  return adminApi.get(`${API_URL}/admin/tickets/my-tickets`)
}

// Notification-bell feed: my active assigned tickets + how many are unseen ("new")
export interface NotificationTicket {
  _id: string
  ticket_number: string
  subject: string
  priority: TicketPriority
  status: TicketStatus
  department: string | null
  created_at: string
  follow_up_date: string | null
  account: { first_name: string; last_name: string } | null
  is_new: boolean
}
export function getMyNotifications(): Promise<{ data: { newCount: number; total: number; tickets: NotificationTicket[] } }> {
  return adminApi.get(`${API_URL}/admin/tickets/my-notifications`)
}
export function markNotificationsSeen(): Promise<{ data: { success: boolean } }> {
  return adminApi.post(`${API_URL}/admin/tickets/my-notifications/seen`)
}

// Get unassigned tickets
export function getUnassignedTickets(): Promise<{ data: { tickets: Ticket[] } }> {
  return adminApi.get(`${API_URL}/admin/tickets/unassigned`)
}

// Get single ticket with full customer profile
export function getTicketDetail(ticketId: string): Promise<{ data: TicketDetailResponse }> {
  return adminApi.get(`${API_URL}/admin/tickets/${ticketId}`)
}

// Get messages only (lightweight, for polling)
export function getTicketMessages(ticketId: string): Promise<{ data: { messages: TicketMessage[] } }> {
  return adminApi.get(`${API_URL}/admin/tickets/${ticketId}/messages`)
}

// Add reply to ticket
export function addTicketReply(
  ticketId: string,
  message: string,
  isInternalNote: boolean = false,
  attachments?: File[]
): Promise<{ data: { success: boolean; message: TicketMessage } }> {
  const formData = new FormData()
  formData.append('message', message)
  formData.append('is_internal_note', String(isInternalNote))

  if (attachments) {
    attachments.forEach(file => {
      formData.append('attachments', file)
    })
  }

  return adminApi.post(`${API_URL}/admin/tickets/${ticketId}/reply`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  })
}

// Update ticket (status, priority, tags)
export function updateTicket(
  ticketId: string,
  data: {
    status?: TicketStatus
    priority?: TicketPriority
    tags?: string[]
    follow_up_date?: string | null
    follow_up_note?: string
    department?: string | null
  }
): Promise<{ data: { success: boolean; ticket: Ticket } }> {
  return adminApi.put(`${API_URL}/admin/tickets/${ticketId}`, data)
}

// Assign ticket to admin
export function assignTicket(
  ticketId: string,
  adminId: string | null
): Promise<{ data: { success: boolean; ticket: Ticket } }> {
  return adminApi.put(`${API_URL}/admin/tickets/${ticketId}/assign`, { admin_id: adminId })
}

// Reassign ticket to another admin
export function reassignTicket(
  ticketId: string,
  adminId: string,
  note?: string
): Promise<{ data: { success: boolean; ticket: Ticket } }> {
  return adminApi.put(`${API_URL}/admin/tickets/${ticketId}/reassign`, { admin_id: adminId, note })
}

// Bulk assign tickets
export function bulkAssignTickets(
  ticketIds: string[],
  adminId: string | null
): Promise<{ data: { success: boolean; updated: number } }> {
  return adminApi.post(`${API_URL}/admin/tickets/bulk/assign`, { ticket_ids: ticketIds, admin_id: adminId })
}

// Bulk update status
export function bulkUpdateStatus(
  ticketIds: string[],
  status: TicketStatus
): Promise<{ data: { success: boolean; updated: number } }> {
  return adminApi.post(`${API_URL}/admin/tickets/bulk/status`, { ticket_ids: ticketIds, status })
}

// ============================================
// CREATE TICKET
// ============================================

export function createTicketByAdmin(
  data: {
    subject: string
    message?: string
    account_id?: string
    department?: string
    priority?: TicketPriority
    is_internal?: boolean
    assigned_to?: string
    task_type?: string
    follow_up_date?: string
  }
): Promise<{ data: { success: boolean; ticket: Ticket } }> {
  return adminApi.post(`${API_URL}/admin/tickets/create`, data)
}

// Reorder tickets within a kanban column
export function reorderTickets(
  orders: { ticketId: string; kanban_order: number }[]
): Promise<{ data: { success: boolean } }> {
  return adminApi.post(`${API_URL}/admin/tickets/reorder`, { orders })
}

// Login as account (switch account) - returns JWT token
export function switchToAccount(
  accountId: string
): Promise<{ data: { success: boolean; token: string } }> {
  return adminApi.get(`${API_URL}/admin/switch-account/${accountId}`)
}

// ============================================
// TAG MANAGEMENT
// ============================================

export function getTags(): Promise<{ data: { tags: TicketTag[] } }> {
  return adminApi.get(`${API_URL}/admin/tickets/tags/list`)
}

export function createTag(
  data: { name: string; color?: string; description?: string }
): Promise<{ data: { success: boolean; tag: TicketTag } }> {
  return adminApi.post(`${API_URL}/admin/tickets/tags`, data)
}

export function updateTag(
  tagId: string,
  data: { name?: string; color?: string; description?: string; is_active?: boolean }
): Promise<{ data: { success: boolean; tag: TicketTag } }> {
  return adminApi.put(`${API_URL}/admin/tickets/tags/${tagId}`, data)
}

export function deleteTag(tagId: string): Promise<{ data: { success: boolean } }> {
  return adminApi.delete(`${API_URL}/admin/tickets/tags/${tagId}`)
}

// ============================================
// ADMIN USERS LIST
// ============================================

export function getAdminUsers(): Promise<{ data: { admins: AdminUser[] } }> {
  return adminApi.get(`${API_URL}/admin/tickets/admins/list`)
}

// ============================================
// INTERNAL TASKS ENDPOINTS
// ============================================

export function getTaskStats(): Promise<{ data: TaskStats }> {
  return adminApi.get(`${API_URL}/admin/tickets/tasks/stats`)
}

export function getMyTasks(): Promise<{ data: { tasks: Ticket[] } }> {
  return adminApi.get(`${API_URL}/admin/tickets/tasks/my-tasks`)
}

export function getAllTasks(accountId?: string): Promise<{ data: { tasks: Ticket[] } }> {
  const params = accountId ? { account_id: accountId } : {}
  return adminApi.get(`${API_URL}/admin/tickets/tasks/all`, { params })
}

export function getTaskFollowUps(): Promise<{ data: { tasks: Ticket[] } }> {
  return adminApi.get(`${API_URL}/admin/tickets/tasks/follow-ups`)
}

export function getTaskAccountCounts(): Promise<{ data: { counts: Record<string, number> } }> {
  return adminApi.get(`${API_URL}/admin/tickets/tasks/account-counts`)
}

export function snoozeTask(
  ticketId: string,
  data: { preset?: string; custom_date?: string }
): Promise<{ data: { success: boolean; ticket: Ticket } }> {
  return adminApi.put(`${API_URL}/admin/tickets/${ticketId}/snooze`, data)
}
